import { useQuery } from '@tanstack/react-query'
import { kaseyaAPI, type KaseyaGroup } from '../../services/kaseya-api'
import { supabase } from '../../lib/supabase'

/**
 * Hook to fetch Kaseya groups for a specific company
 * Requires the company to have a kaseya_organization_id
 * Returns groups and the company's default group ID
 */
export const useKaseyaGroups = (companyId: string | null) => {
  return useQuery({
    queryKey: ['kaseya-groups', companyId],
    queryFn: async () => {
      if (!companyId) return { groups: [], defaultGroupId: null }

      // Get company's Kaseya organization ID and default group
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('kaseya_organization_id, default_kaseya_group_id, kaseya_group_id')
        .eq('id', companyId)
        .single()

      if (companyError) {
        console.error('Failed to fetch company:', companyError)
        throw new Error('Company not found')
      }

      if (!company.kaseya_organization_id) {
        console.warn(`Company ${companyId} is not linked to a Kaseya organization`)
        return { groups: [], defaultGroupId: null }
      }

      // Fetch groups from Kaseya API
      const groups = await kaseyaAPI.getOrganizationGroups(company.kaseya_organization_id)

      // Transform to simpler format for dropdowns
      const mappedGroups = groups.map((g: KaseyaGroup) => ({
        id: g.Id,
        name: g.Name,
        organization_id: g.ParentOrganizationId
      }))

      // Prefer default_kaseya_group_id, fallback to kaseya_group_id
      const defaultGroupId = company.default_kaseya_group_id || company.kaseya_group_id || null

      return {
        groups: mappedGroups,
        defaultGroupId
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
